import { useMemo } from 'react';
import { Configuration, ProductListsApi } from '@/api';
import { productListQueryKeys } from '@/api/queries/productListQueryKeys';
import { ProductList } from '@/models/ProductList';
import { useAuth } from '@/modules/auth/contexts/AuthContext';
import { useQuery, useQueryClient } from '@tanstack/react-query';

interface GetProductListQueryResult {
  productList?: ProductList;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  invalidateQuery: () => Promise<void>;
}

const useGetProductListQuery = (productListId?: string): GetProductListQueryResult => {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const productListsApi = useMemo(() => new ProductListsApi(new Configuration(auth)), [auth]);
  const queryKey = productListQueryKeys.detail(productListId);

  const {
    data: productList,
    isLoading,
    isFetching,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => {
      if (!productListId) throw new Error('Missing productListId');
      return productListsApi.getProductList(productListId);
    },
    enabled: Boolean(productListId),
  });

  const invalidateQuery = () => queryClient.invalidateQueries({ queryKey });

  return { productList, isLoading, isFetching, error, invalidateQuery };
};

export default useGetProductListQuery;
